import { useDynamicPageContext } from '@/contexts/DynamicPageContext';
import * as Sentry from '@sentry/react';
import { DynamicPageVersion, DynamicPageVersionDiff } from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@superfunnel/ui-core/v2/Drawer';
import Input from '@superfunnel/ui-core/v2/Input';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { diffDynamicPageVersions } from '@superfunnel/utils';
import { Xmark } from 'iconoir-react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  createDynamicPageVersion,
  createPreviewDynamicPageVersion,
  getDynamicPageVersionContents,
  getPrevDynamicPageVersion,
} from '../services/DynamicPagesService';
import { capitalize } from '../utils';

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function PublishDynamicPageVersionDrawer({
  open,
  setOpen,
}: Props) {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();

  const [prevVersion, setPrevVersion] = useState<DynamicPageVersion | null>();
  const [newVersion, setNewVersion] = useState<DynamicPageVersion>();
  const [isLoadingPublish, setIsLoadingPublish] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [versionDescription, setVersionDescription] = useState('');

  const [error, setError] = useState<Error>();

  const versionsDiff: DynamicPageVersionDiff[] = useMemo(() => {
    if (!prevVersion || !newVersion) return [];

    return diffDynamicPageVersions(prevVersion, newVersion);
  }, [prevVersion, newVersion]);

  useEffect(() => {
    setPrevVersion(undefined);

    if (!open) return;
    if (!selectedAccount) return;
    if (!selectedDynamicPage) return;

    let relevant = true;
    getPrevDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id).then(
      async (versionDocs) => {
        if (!relevant) return;
        const versionMetadata = versionDocs?.[0];
        if (!versionMetadata) {
          setPrevVersion(null);
          return;
        }

        const metadata = versionMetadata.data();
        // Check if version has storageUrl (new format) or inline data (legacy)
        if (metadata.storageUrl) {
          const fullVersion = await getDynamicPageVersionContents(
            selectedAccount.id,
            selectedDynamicPage.id,
            versionMetadata.id,
          );
          if (!relevant) return;
          setPrevVersion(fullVersion);
        } else {
          // Legacy version with inline data
          if (!relevant) return;
          setPrevVersion(metadata as unknown as DynamicPageVersion);
        }
      },
    );

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    setError(undefined);
    setNewVersion(undefined);
    setVersionName('');
    setVersionDescription('');

    if (!open) return;
    if (!selectedDynamicPage) return;
    if (!selectedAccount) return;

    let relevant = true;
    createPreviewDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id)
      .then((version) => {
        if (!relevant) return;
        setNewVersion(version);
      })
      .catch((e) => {
        if (!relevant) return;
        Sentry.captureException(e);
        console.error('Failed to create new version:', e);
        setError(e);
      });

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  const isPublishButtonDisabled = useMemo(() => {
    return !newVersion || !versionName.trim() || prevVersion === undefined;
  }, [newVersion, prevVersion, versionName]);

  const onPublishVersionButtonClick = useCallback(() => {
    if (!selectedDynamicPage || !selectedAccount || !newVersion) {
      show('Failed to publish version', 'warn');
      return;
    }

    if (!versionName.trim()) {
      show('Version name is required', 'warn');
      return;
    }

    setIsLoadingPublish(true);
    createDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id, {
      ...newVersion,
      name: versionName.trim(),
      description: versionDescription?.trim(),
      changes: JSON.parse(JSON.stringify(versionsDiff)),
    })
      .then(() => {
        show('Version published', 'success');
        setOpen(false);
      })
      .catch((e) => {
        console.error('Failed to publish version:', e);
        Sentry.captureException(e);
        show('Failed to publish version', 'warn');
      })
      .finally(() => {
        setIsLoadingPublish(false);
      });
  }, [
    selectedDynamicPage,
    selectedAccount,
    newVersion,
    versionName,
    versionDescription,
    versionsDiff,
    show,
    setOpen,
  ]);

  return (
    <>
      <Drawer open={open} onOpenChange={setOpen} direction="right">
        <DrawerContent>
          <div className="flex h-full w-full flex-col">
            <div className={'flex items-start justify-between gap-2'}>
              <DrawerHeader className={'-tracking-[0.01em]'}>
                <DrawerTitle>Create a New Dynamic Page Version</DrawerTitle>
                <DrawerDescription>
                  Publish a new version of the dynamic page to make it live.
                </DrawerDescription>
              </DrawerHeader>
              <DrawerClose className={'shrink-0 py-3'}>
                <Xmark className={'size-5'} />
              </DrawerClose>
            </div>
            <div className="border-base-200 relative inset-0 grow overflow-y-auto border-t pt-4.5">
              <form className="flex h-full flex-col">
                <div className="flex-1">
                  {/* Divider container */}
                  {!newVersion && error && (
                    <div className="text-sm/6 text-red-600">
                      {error.message || 'Failed to create version'}
                    </div>
                  )}

                  <DynamicPageVersionDetailsForm
                    versionName={versionName}
                    onVersionNameChange={setVersionName}
                    versionDescription={versionDescription}
                    onVersionDescriptionChange={setVersionDescription}
                    changes={versionsDiff}
                    loading={!newVersion || prevVersion === undefined}
                    hasPrevVersion={!!prevVersion}
                    readOnly={false}
                    disabled={false}
                  />
                </div>

                {/* Action buttons */}
                <div className="shrink-0 py-2.5">
                  <div className="flex justify-between space-x-3">
                    <Button
                      type={'button'}
                      variant="outline"
                      onClick={() => setOpen(false)}
                      className={'rounded-2lg grow'}
                    >
                      Discard
                    </Button>
                    <Button
                      className={'rounded-2lg grow'}
                      loading={isLoadingPublish}
                      disabled={isPublishButtonDisabled}
                      onClick={onPublishVersionButtonClick}
                    >
                      Publish
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

interface DynamicPageVersionDetailsFormProps {
  versionName: string;
  onVersionNameChange?: (name: string) => void;
  versionDescription: string;
  onVersionDescriptionChange?: (description: string) => void;
  loading: boolean;
  readOnly: boolean;
  disabled: boolean;
  changes: DynamicPageVersionDiff[];
  hasPrevVersion: boolean | undefined;
}
export const DynamicPageVersionDetailsForm = ({
  versionName,
  onVersionNameChange,
  versionDescription,
  onVersionDescriptionChange,
  loading,
  readOnly,
  disabled,
  changes,
  hasPrevVersion,
}: DynamicPageVersionDetailsFormProps) => {
  const { isSupportAdmin } = useContext(AuthContext);
  const versionsDiff = changes;

  return (
    <div className="font-inter space-y-4">
      {/* Version name */}
      <Input
        title="Version Name"
        readOnly={readOnly}
        disabled={disabled}
        value={versionName}
        onChange={(e) => {
          onVersionNameChange?.(e.target.value);
        }}
      />

      {/* Version description */}
      <TextArea
        id="version-description"
        name="version-description"
        title="Description (optional)"
        readOnly={readOnly}
        disabled={disabled}
        value={versionDescription}
        className={'min-h-30'}
        rows={3}
        onChange={(e) => {
          onVersionDescriptionChange?.(e.target.value);
        }}
      />

      {/* Changes */}
      <div className="space-y-2">
        <div
          aria-hidden="true"
          className="text-base-700 block text-sm font-medium"
        >
          Changes
        </div>
        <div className="space-y-5">
          {loading && (
            <div className="flex gap-2">
              <Loader />
              <span className="text-base-500 text-sm">Loading changes...</span>
            </div>
          )}
          {!loading && !versionsDiff.length && !hasPrevVersion && (
            <div className={'text-base-500 text-sm'}>
              This is the first version
            </div>
          )}
          {!loading && !versionsDiff.length && hasPrevVersion && (
            <div className={'text-base-500 text-sm'}>No changes</div>
          )}
          {!loading && !!versionsDiff.length && (
            <ul role="list" className={'font-inter space-y-2 text-sm'}>
              {versionsDiff.map((diff) => (
                <li
                  key={`${diff.id}-${diff.type}-${diff.entity}`}
                  className="bg-base-100 flex items-center justify-between rounded-lg px-3 py-2"
                >
                  <Tooltip
                    open={
                      !isSupportAdmin || diff.type !== 'changed'
                        ? false
                        : undefined
                    }
                  >
                    <TooltipTrigger asChild>
                      <div
                        className="flex w-0 flex-1 items-center"
                        onClick={() => {
                          if (!isSupportAdmin) return;
                          if (diff.type !== 'changed') return;
                          // copy diff to clipboard
                          navigator.clipboard.writeText(
                            JSON.stringify(diff.diff, undefined, 2),
                          );
                        }}
                      >
                        <div className="flex min-w-0 flex-1 items-center justify-between gap-6 leading-normal -tracking-[0.01em]">
                          <span>{diff.explanation}</span>
                          <span className="text-base-500 shrink-0 text-xs">
                            {capitalize(diff.type)}
                          </span>
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>
                        {diff.type === 'changed'
                          ? JSON.stringify(diff.diff, undefined, 2)
                          : ''}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
